import { Topic } from 'shared/models/legacy';
import { Link } from '@remix-run/react';
import { FC, useMemo, useCallback } from 'react';
import { StepScroll } from '../step-scroll';
import { TopicArticleCard } from './topic-article-card';
import NumberIcon from '@lemon8/design/lib/icons/icon_assets/number_fill.svg';
import { useAtomValue } from 'jotai';
import { $Locale } from '@lemon8/web-app-shared/atom';
import { useTranslation } from 'react-i18next';
import { useNumFormatter } from '~/utils/format/num-formatter';
import { ClickPosition, ClickPositionPrefix } from '~/utils/click-position';
import { usePageActive } from '~/utils/active/use-page-active';
import useReflowClick from '~/utils/use-reflow-click';
import { EPageType } from 'shared/models';
import { $IsMobileUser } from '~/utils/is-mobile-user';
import { useScopedAtomValue } from '@lemon8/web-app-shared/ssr';
import { $CurrentPageModule } from '~/models/current-page-module';
import { useViewTracking } from '~/utils/use-view-tracking';
import { kepQueryFormat } from 'shared/utils/format/kep-formatter';
import { SEOKeywordsItem } from '../seo-keywords-item';
import { $SEOCurrentTraffic } from 'shared/atoms/common/traffic';

import './style.scss';

export const MIN_HASHTAG_ARTICLE_COUNT = 3;
const MAX_HASHTAG_ARTICLE_COUNT = 10;

interface TopicCardProps {
  topic: Topic | string;
  positionPrefix?: ClickPositionPrefix;
}

export const TopicCard: FC<TopicCardProps> = ({ topic, positionPrefix = ClickPositionPrefix.none }) => {
  const { pageType } = useAtomValue($SEOCurrentTraffic);
  const isKEPPage = pageType === EPageType.discover;

  const isOnlyWord = typeof topic === 'string';
  const { t } = useTranslation();
  const { region } = useAtomValue($Locale);
  const formatNum = useNumFormatter();
  const active = usePageActive();

  const isMobileUser = useAtomValue($IsMobileUser);
  const currentPageModule = useScopedAtomValue($CurrentPageModule);

  const topicHref = useMemo(() => {
    if (!isKEPPage || isOnlyWord) {
      return `/discover/${kepQueryFormat(isOnlyWord ? topic : topic.topicName)}?region=${region}`;
    }
    return `/topic/${topic.topicId}?region=${region}`;
  }, [topic, region, isKEPPage]);

  const isHashtagWithArticleCard = useMemo(() => {
    return !isOnlyWord && topic.articles && topic.articles.length >= MIN_HASHTAG_ARTICLE_COUNT;
  }, []);

  const currentPosition = useMemo(() => {
    return positionPrefix + isHashtagWithArticleCard
      ? ClickPosition.related_hashtag_with_article_card
      : ClickPosition.related_hashtag;
  }, []);

  const handleHashtagClick = useReflowClick(
    {
      clickEvent: (_, bundle) => ({
        ...bundle,
        current_position: currentPosition,
        page_click_id: isOnlyWord ? kepQueryFormat(topic) : topic.topicId,
      }),
      reflowBehavior: () => {
        if (isOnlyWord) {
          active({
            schemaOption: {
              pageType: 'search',
              query: kepQueryFormat(topic),
            },
            ampExtra: {
              enter_position: currentPosition,
            },
          });
        } else {
          active({
            schemaOption: {
              pageType: 'hashtag',
              hashtagId: topic.topicId,
            },
            ampExtra: {
              enter_position: currentPosition,
            },
          });
        }
      },
    },
    [active, topic, isOnlyWord],
  );

  const description = useMemo(() => {
    return (
      isKEPPage &&
      !isOnlyWord &&
      t('view_count_plural', { num: topic.viewCount }).replace('%d', formatNum(topic.viewCount))
    );
  }, []);

  const hashtagRef = useViewTracking<HTMLAnchorElement>({
    onView: useCallback(() => {
      if (isMobileUser) {
        window.Tea('wap_cta_show', {
          current_page_module: currentPageModule,
          current_position: currentPosition,
        });
      }
    }, [isMobileUser]),
  });

  return (
    <Link className="hashtag-card" to={topicHref} draggable={false} onClick={handleHashtagClick} ref={hashtagRef}>
      <div className="hashtag-card-head">
        <div className="hashtag-card-head-icon">
          <NumberIcon />
        </div>
        <div className="hashtag-card-head-main">
          <p className="hashtag-card-head-main-title">
            <SEOKeywordsItem keyword={isOnlyWord ? topic : topic.topicName} />
          </p>
          {description && <p className="hashtag-card-head-main-desc">{description}</p>}
        </div>
      </div>
      {!isOnlyWord && topic.articles && topic.articles.length >= MIN_HASHTAG_ARTICLE_COUNT && (
        <StepScroll className="hashtag-card-articles">
          {topic.articles.slice(0, MAX_HASHTAG_ARTICLE_COUNT).map((article) => (
            <TopicArticleCard key={article.groupId} topic={topic} article={article} positionPrefix={positionPrefix} />
          ))}
        </StepScroll>
      )}
    </Link>
  );
};
